import { FC, Fragment, MouseEvent, ReactNode, useState } from "react";
import { Collapse, Dropdown } from "react-bootstrap";
import { BoxArrowUpRight, CaretDownFill, CaretUpFill, Pencil } from "react-bootstrap-icons";
import { Link } from "react-router-dom";

import { ActionMenu } from "src/components/actionMenu.tsx";
import { ContainerList } from "src/components/subscriptionList/containerList.tsx";
import { SubscriptionRow } from "src/components/subscriptionList/subscriptionList.tsx";
import { SubscriptionColumn } from "src/components/subscriptionList/subscriptionListColumns.ts";
import { useUserState } from "src/store/store.ts";
import { useBackUrl } from "src/utils/useBackUrl.ts";

interface SubscriptionListRowProps {
  subscriptionRow: SubscriptionRow;
  orderedAndFilteredColumns: SubscriptionColumn[];
}

export const SubscriptionListRow: FC<SubscriptionListRowProps> = ({ subscriptionRow, orderedAndFilteredColumns }) => {
  const [openedSubscriptions, setOpenedSubscriptions] = useState<string[]>([]);
  const user = useUserState();
  const { backUrl } = useBackUrl();

  const buildContextMenu = (sr: SubscriptionRow): ReactNode | null => {
    if (user.role === "Admin") {
      return (
        <td className="text-end">
          <ActionMenu>
            <Dropdown.Item as={Link} to={`/subscriptions/${sr.id}`} className="text-black">
              <Pencil size={14} className="me-2" /> Edit
            </Dropdown.Item>
            {sr.shipmentId !== null ? (
              <Dropdown.Item
                href={`https://app.visiwise.co/shipments/${sr.shipmentId}/tracking/`}
                target="_blank"
                referrerPolicy="no-referrer"
              >
                <BoxArrowUpRight size={14} className="me-2" /> Show on Visiwise
              </Dropdown.Item>
            ) : null}
          </ActionMenu>
        </td>
      );
    }
    if (user.rights.includes("CreateSubscription")) {
      return (
        <td className="text-end">
          <Link to={`/subscriptions/${sr.id}`} state={{ backUrl: backUrl }}>
            <Pencil size={14} className="me-2" />
          </Link>
        </td>
      );
    } else {
      return null;
    }
  };

  const toggleSubscriptionState = (event: MouseEvent<HTMLTableCellElement>, subscriptionId: string) => {
    if (!(event.target instanceof HTMLAnchorElement) && !document.getSelection()?.toString())
      setOpenedSubscriptions((prevState) => {
        const index = prevState.indexOf(subscriptionId);
        if (index >= 0) {
          return prevState.toSpliced(index, 1);
        } else {
          return [...prevState, subscriptionId];
        }
      });
  };

  return (
    <Fragment>
      <tr>
        {orderedAndFilteredColumns.map((column) => (
          <td
            key={`td-${column.key}`}
            className={subscriptionRow.done ? "text-body-tertiary" : undefined}
            onClick={(event) => toggleSubscriptionState(event, subscriptionRow.id)}
          >
            {subscriptionRow[column.key]}
          </td>
        ))}
        <td onClick={(event) => toggleSubscriptionState(event, subscriptionRow.id)} className="text-end">
          {subscriptionRow.containers.length > 0 ? (
            openedSubscriptions.includes(subscriptionRow.id) ? (
              <CaretUpFill role="button" />
            ) : (
              <CaretDownFill role="button" />
            )
          ) : null}
        </td>
        {buildContextMenu(subscriptionRow)}
      </tr>
      {subscriptionRow.containers.length > 0 && (
        <tr>
          <td
            colSpan={orderedAndFilteredColumns.length + (buildContextMenu(subscriptionRow) !== null ? 2 : 1)}
            className="p-0 border-bottom-0"
          >
            <Collapse in={openedSubscriptions.includes(subscriptionRow.id)} mountOnEnter={true} unmountOnExit={true}>
              <div className="border-bottom">
                <div className="m-3">
                  <ContainerList subscriptionId={subscriptionRow.id} containers={subscriptionRow.containers} />
                </div>
              </div>
            </Collapse>
          </td>
        </tr>
      )}
    </Fragment>
  );
};
